import type { LeadDaLista } from '@titularis/api-client';
import type { api } from '../../api';
import { formatarData } from '../../dominio';

/*
 * O vocabulário é aberto no servidor. O mapa traduz o que já conhecemos, mas
 * não valida o filtro: exigir um nome daqui esconderia a demanda de um tópico
 * novo até o próximo deploy. A tabela, o resumo, o CSV e a ficha da pessoa usam
 * o mesmo rótulo - a ficha tinha um mapa próprio, com "1ª fase" onde a aba
 * dizia "Fila da 1ª fase", até 25/09/2026.
 */
export const ROTULOS_DAS_LISTAS = new Map<string, string>([
  ['novidades', 'Novidades'],
  ['modulo:objective', 'Fila da 1ª fase'],
  ['modulo:essay', 'Fila da 2ª fase'],
  ['mapa-do-enac', 'Mapa do Enac'],
]);

const ROTULOS_DAS_ORIGENS = new Map<string, string>([
  ['landing', 'Página inicial'],
  ['provas', 'Provas'],
  ['noticias', 'Notícias'],
  ['revista', 'Revista'],
  ['app', 'Aplicativo'],
]);

export const rotuloDaLista = (topico: string): string =>
  ROTULOS_DAS_LISTAS.get(topico) ?? topico;

export const rotuloDaOrigem = (origem: string): string =>
  ROTULOS_DAS_ORIGENS.get(origem) ?? origem;

const TAMANHO_DA_PAGINA_CSV = 200;
const MAXIMO_DE_PAGINAS_CSV = 50;

/** O que a tela está mostrando: o tópico e se as contas internas entram. */
export interface RecorteDasListas {
  topico?: string;
  semInternas: boolean;
}

/**
 * Exportar é levar o recorte inteiro, inclusive quem saiu, e não a página que
 * estava aberta. Duzentos é o máximo aceito pela API; o teto de páginas protege
 * de um total incorreto. Se não completar, falha em vez de baixar um arquivo
 * parcial com aparência de completo.
 *
 * O recorte das contas internas vai junto: o arquivo tem de ter as mesmas
 * linhas da tabela, e a equipe que testou o formulário não é destinatária.
 */
export async function buscarListasParaCsv(
  buscarPagina: typeof api.listaDeEspera,
  recorte: RecorteDasListas,
): Promise<LeadDaLista[]> {
  const pessoas: LeadDaLista[] = [];

  for (let pagina = 1; pagina <= MAXIMO_DE_PAGINAS_CSV; pagina += 1) {
    const resposta = await buscarPagina({
      ...(recorte.topico ? { topic: recorte.topico } : {}),
      ...(recorte.semInternas ? { semInternas: true } : {}),
      page: pagina,
      pageSize: TAMANHO_DA_PAGINA_CSV,
    });
    pessoas.push(...resposta.itens);
    if (pessoas.length >= resposta.total) return pessoas;

    if (resposta.itens.length === 0) {
      throw new Error('A lista mudou durante a exportação. Tente novamente.');
    }
  }

  throw new Error('O recorte excede o limite da exportação. Selecione uma lista menor.');
}

/*
 * O destino é o Excel em português: vírgula junta tudo numa célula, e sem o
 * BOM os acentos se perdem. Aspas protegem separadores e quebras de linha de
 * tópicos futuros, cujo conteúdo não está limitado ao vocabulário de hoje.
 */
function escaparCampoCsv(valor: string): string {
  return /[;"\r\n]/.test(valor) ? `"${valor.replace(/"/g, '""')}"` : valor;
}

export function montarCsvDasListas(pessoas: LeadDaLista[]): string {
  const linhas = pessoas.map((pessoa) =>
    [
      pessoa.email,
      rotuloDaLista(pessoa.topic),
      rotuloDaOrigem(pessoa.source),
      pessoa.userId ? 'sim' : 'não',
      formatarData(pessoa.createdAt),
      pessoa.unsubscribedAt ? formatarData(pessoa.unsubscribedAt) : '',
    ].map(escaparCampoCsv).join(';'),
  );

  return '\ufeff' + ['E-mail;Lista;Origem;Tem conta;Entrou em;Saiu em', ...linhas].join('\r\n');
}

export function nomeDoCsvDasListas(topico?: string, data = new Date()): string {
  // O dia é o de quem exportou. UTC trocaria a data antes da meia-noite local.
  const dia = [
    data.getFullYear(),
    String(data.getMonth() + 1).padStart(2, '0'),
    String(data.getDate()).padStart(2, '0'),
  ].join('-');
  // Dois-pontos identifica os módulos, mas não cabe em nome de arquivo no Windows.
  const recorte = (topico || 'todas').replace(/[<>:"/\\|?*]/g, '-');
  return `listas-${recorte}-${dia}.csv`;
}
